import { fetchWithTimeout } from '../../utils/fetchWithTimeout'

class FilterBar {
  private readonly bars: NodeListOf<HTMLElement>

  constructor() {
    this.bars = document.querySelectorAll<HTMLElement>('.js-filter-bar')

    if (this.bars.length === 0) return

    this.bars.forEach((bar) => {
      this.setupBar(bar)
    })
  }

  private setupBar(bar: HTMLElement): void {
    const endpoint = bar.dataset.filterEndpoint
    const resultsId = bar.dataset.filterResultsId

    if (endpoint == null || endpoint === '' || resultsId == null) return

    const results = document.getElementById(resultsId)
    if (results == null) return

    const announcer = bar.querySelector<HTMLElement>('.js-filter-bar-announcer')
    const neighbourhoodSelect = bar.querySelector<HTMLSelectElement>('.js-filter-bar-neighbourhood')
    const pillGroups = bar.querySelectorAll<HTMLElement>('.js-filter-bar-pills')
    const summary = results.parentElement?.querySelector<HTMLElement>('.js-filter-bar-summary')

    announcer?.setAttribute('aria-atomic', 'true')
    const summaryCount = summary?.querySelector<HTMLElement>('.js-filter-bar-summary-count') ?? null
    const summaryFilter =
      summary?.querySelector<HTMLElement>('.js-filter-bar-summary-filter') ?? null
    const style = bar.dataset.filterStyle
    // The endpoint has no listing URL to read the page's content_types from, so it comes from
    // the markup and rides along on every request.
    const contentTypes = bar.dataset.filterContentTypes

    const requestResults = (): void => {
      void this.fetchResults(
        endpoint,
        style,
        contentTypes,
        neighbourhoodSelect,
        pillGroups,
        results,
        announcer,
        summaryCount,
        summaryFilter
      )
    }

    neighbourhoodSelect?.addEventListener('change', requestResults)

    pillGroups.forEach((group) => {
      group.addEventListener('change', (event) => {
        if ((event.target as HTMLElement).classList.contains('filter-bar-pill-input')) {
          requestResults()
        }
      })
    })

    // Delegated so the handler survives filter-change replacements of the results markup.
    results.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>(
        '.js-filter-bar-load-more'
      )
      if (button == null || !results.contains(button)) return
      if (button.getAttribute('aria-disabled') === 'true') return

      event.preventDefault()
      void this.loadMore(
        button,
        endpoint,
        style,
        contentTypes,
        neighbourhoodSelect,
        pillGroups,
        results,
        announcer,
        summaryCount,
        summaryFilter
      )
    })
  }

  private buildParams(
    style: string | undefined,
    contentTypes: string | undefined,
    neighbourhoodSelect: HTMLSelectElement | null,
    pillGroups: NodeListOf<HTMLElement>
  ): URLSearchParams {
    const params = new URLSearchParams()

    if (style != null && style !== '') params.set('style', style)

    if (contentTypes != null && contentTypes !== '') params.set('contentTypes', contentTypes)

    if (neighbourhoodSelect != null && neighbourhoodSelect.value !== '') {
      params.set('neighbourhoods', neighbourhoodSelect.value)
    }

    pillGroups.forEach((group) => {
      const groupName = group.dataset.filterGroup
      const checked = group.querySelector<HTMLInputElement>('.filter-bar-pill-input:checked')

      if (groupName == null || checked == null || checked.value === '') return

      params.set(groupName, checked.value)
    })

    return params
  }

  private async fetchResults(
    endpoint: string,
    style: string | undefined,
    contentTypes: string | undefined,
    neighbourhoodSelect: HTMLSelectElement | null,
    pillGroups: NodeListOf<HTMLElement>,
    results: HTMLElement,
    announcer: HTMLElement | null,
    summaryCount: HTMLElement | null,
    summaryFilter: HTMLElement | null
  ): Promise<void> {
    const params = this.buildParams(style, contentTypes, neighbourhoodSelect, pillGroups)

    results.setAttribute('aria-busy', 'true')
    this.announce(announcer, 'Updating results…')

    try {
      const response = await fetchWithTimeout(`${endpoint}?${params.toString()}`)

      if (!response.ok) throw new Error(`Unexpected status ${response.status}`)

      results.innerHTML = await response.text()
      this.updateSummary(results, neighbourhoodSelect, pillGroups, summaryCount, summaryFilter)
      this.announce(announcer, 'Results updated.')
    } catch {
      this.announce(announcer, 'Sorry, we could not update the results. Please try again.')
    } finally {
      results.removeAttribute('aria-busy')
    }
  }

  private async loadMore(
    button: HTMLAnchorElement,
    endpoint: string,
    style: string | undefined,
    contentTypes: string | undefined,
    neighbourhoodSelect: HTMLSelectElement | null,
    pillGroups: NodeListOf<HTMLElement>,
    results: HTMLElement,
    announcer: HTMLElement | null,
    summaryCount: HTMLElement | null,
    summaryFilter: HTMLElement | null
  ): Promise<void> {
    const skip = button.dataset.loadMoreSkip
    if (skip == null || skip === '') return

    const params = this.buildParams(style, contentTypes, neighbourhoodSelect, pillGroups)
    params.set('skip', skip)

    button.setAttribute('aria-disabled', 'true')
    button.setAttribute('aria-busy', 'true')
    button.classList.add('is-loading')
    results.setAttribute('aria-busy', 'true')
    this.announce(announcer, 'Loading more results…')

    try {
      const response = await fetchWithTimeout(`${endpoint}?${params.toString()}`)

      if (!response.ok) throw new Error(`Unexpected status ${response.status}`)

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html')
      const list = results.querySelector<HTMLElement>('.article-card-list')
      const newRows = parsed.querySelectorAll<HTMLElement>('.article-card-list-row')

      if (list == null || newRows.length === 0) {
        // Fallback if the existing list is missing (e.g. previous state was the empty-state message).
        results.innerHTML = parsed.body.innerHTML
      } else {
        newRows.forEach((row) => list.appendChild(row))
      }

      const nextButtonWrap = parsed.querySelector<HTMLElement>('.filter-bar-load-more')
      const oldWrapper = button.closest<HTMLElement>('.filter-bar-load-more') ?? button
      if (nextButtonWrap != null) {
        oldWrapper.replaceWith(nextButtonWrap)
        nextButtonWrap.querySelector<HTMLAnchorElement>('.js-filter-bar-load-more')?.focus()
      } else {
        oldWrapper.remove()
        results
          .querySelector<HTMLAnchorElement>('.article-card-list-row:last-child .article-card-link')
          ?.focus()
      }

      this.updateSummary(results, neighbourhoodSelect, pillGroups, summaryCount, summaryFilter)
      this.announce(announcer, 'More results loaded.')
    } catch {
      button.removeAttribute('aria-disabled')
      button.removeAttribute('aria-busy')
      button.classList.remove('is-loading')
      this.announce(announcer, 'Sorry, we could not load more results. Please try again.')
    } finally {
      results.removeAttribute('aria-busy')
    }
  }

  private updateSummary(
    results: HTMLElement,
    neighbourhoodSelect: HTMLSelectElement | null,
    pillGroups: NodeListOf<HTMLElement>,
    summaryCount: HTMLElement | null,
    summaryFilter: HTMLElement | null
  ): void {
    if (summaryCount != null) {
      summaryCount.textContent = String(results.querySelectorAll('.article-card-list-row').length)
    }

    if (summaryFilter != null) {
      summaryFilter.textContent = this.getActiveFilterLabel(neighbourhoodSelect, pillGroups)
    }
  }

  private getActiveFilterLabel(
    neighbourhoodSelect: HTMLSelectElement | null,
    pillGroups: NodeListOf<HTMLElement>
  ): string {
    const activeLabels: string[] = []

    pillGroups.forEach((group) => {
      const checked = group.querySelector<HTMLInputElement>('.filter-bar-pill-input:checked')
      if (checked == null || checked.id.endsWith('-all')) return

      const text = checked.nextElementSibling?.textContent?.trim().toLowerCase()
      if (text != null && text !== '') activeLabels.push(text)
    })

    if (neighbourhoodSelect != null && neighbourhoodSelect.selectedIndex > 0) {
      const text = neighbourhoodSelect.options[neighbourhoodSelect.selectedIndex]?.text.trim()
      if (text != null && text !== '') activeLabels.push(text)
    }

    return activeLabels.join(' ')
  }

  private announce(announcer: HTMLElement | null, message: string): void {
    if (announcer == null) return

    announcer.textContent = ''
    void announcer.offsetHeight
    announcer.textContent = message
  }
}

export default FilterBar
